import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { AuthenticatorData } from '../api/auth';
import { Logger } from '../monitoring';
import { getSessionStorageItemSafely, removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../utils/storage';

interface MfaData {
  mfaToken?: string;
  authenticators?: AuthenticatorData[];
  challengedAuthenticators?: string[];
  /** Which enrollment factor this token belongs to (e.g. 'phone') - carried through to the
   * confirm screen so its copy can reflect the factor that was actually set up. Not used for
   * non-enroll challenges. */
  factor?: string;
  /** The full, unmasked number just entered on the offer screen (enroll flow only) - the verify
   * screen displays this instead of the authenticator's masked description. */
  phoneNumber?: string;
}

export type MfaFlow = 'challenge' | 'enroll';

const MFA_DATA_KEY = 'mfa_data';

// Fallback for when sessionStorage itself throws (blocked storage, sandboxed iframes, some
// in-app browser WebViews). Doesn't survive a page refresh — only bridges the SPA navigation
// from Login/SignUp to the MFA challenge page, which is all the happy path needs.
let inMemoryMfaData: string | null = null;

/**
 * Reads the MFA data saved by navigateToMfaChallenge, falling back to the in-memory copy if
 * sessionStorage was unavailable when it was saved.
 */
export const readStoredMfaData = (): string | null => {
  return getSessionStorageItemSafely(MFA_DATA_KEY) ?? inMemoryMfaData;
};

export const clearStoredMfaData = (): void => {
  removeSessionStorageItemSafely(MFA_DATA_KEY);
  inMemoryMfaData = null;
};

/**
 * Hook that provides a function to save MFA data and navigate to the MFA challenge page.
 */
export const useMfaNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToMfaChallenge = useCallback(
    (mfaData: MfaData, targetPath = '/mfa/challenge') => {
      // Tags the stored token with the flow that requested it, so MFAChallengePage can refuse to
      // treat a challenge-flow token as an enrollment success (or vice versa) if the two diverge -
      // e.g. a stale tab left on /mfa/enroll/verify after the user separately signs in elsewhere.
      const flow: MfaFlow = targetPath === '/mfa/enroll/verify' ? 'enroll' : 'challenge';
      const serialized = JSON.stringify({
        mfaToken: mfaData.mfaToken,
        authenticators: mfaData.authenticators,
        challengedAuthenticators: mfaData.challengedAuthenticators,
        factor: mfaData.factor,
        phoneNumber: mfaData.phoneNumber,
        flow,
      });

      if (!setSessionStorageItemSafely(MFA_DATA_KEY, serialized)) {
        Logger.warn('sessionStorage unavailable for MFA data, falling back to in-memory storage');
        inMemoryMfaData = serialized;
      }

      navigate({ pathname: targetPath, search: location.search });
    },
    [navigate, location.search]
  );

  return { navigateToMfaChallenge };
};
